'use client';

import { Box, Container, Divider, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';

import Avatar from '@/components/Avatar';
import colors from '@/styles/themes/colors';
import { BlogPostWithContent } from '@/types/blog.type';
import DateTime from '@/utils/static/DateTime';

import styles from './SingleBlogContent.module.scss';

type SingleBlogContentProps = Omit<
  BlogPostWithContent,
  'slug' | 'bannerImage' | 'cardImage' | 'featured' | 'title' | 'categories' | 'description'
>;

const SingleBlogContent = ({ content, date, updated, readTime, author }: SingleBlogContentProps) => {
  const locale = useLocale();
  const t = useTranslations('common');

  return (
    <Container component="article" maxWidth={false} classes={{ root: styles.root }} className={styles.containerWrapper}>
      <Container maxWidth="xl" className={styles.container}>
        <Grid container spacing={{ xs: 4, md: 7.5, lg: 10 }} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 'auto' }} order={{ xs: 1, md: 1 }} className={styles.sidebarWrapper}>
            <Box className={styles.sidebar}>
              <Typography variant="subtitle2" component="p" color={colors.graytone400} className={styles.dateInfo}>
                {DateTime.formatBlogDate(dayjs(date), locale)} - {readTime}
              </Typography>
              {updated && (
                <Typography variant="subtitle2" component="p" color={colors.graytone400} className={styles.dateInfo}>
                  {t('blog.updated')} {DateTime.formatBlogDate(dayjs(updated), locale)}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar name={author.name} role={author.role} image={author.avatar} size="xl" />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" component="p" color={colors.white}>
                    {author.name}
                  </Typography>
                  {author.role && (
                    <Typography variant="body1" color={colors.graytone400}>
                      {author.role}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Grid>
          <Grid size={12} order={{ xs: 2, md: 0 }} display={{ xs: 'block', md: 'none' }}>
            <Divider className={styles.divider} />
          </Grid>
          <Grid size={{ xs: 12, md: 'grow' }} order={{ xs: 3, md: 2 }} className={styles.articleBody}>
            <Box className={styles.content} dangerouslySetInnerHTML={{ __html: content }} />
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
};

export default SingleBlogContent;
